import React, { useEffect, useRef, useState } from "react";
import { withRouter, useHistory } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import { Button } from "semantic-ui-react";
import { PLAN_ROUTE, LOGIN_ROUTE, SETTINGS_ROUTE } from "../../utils/common";

import {
  setMfaType,
  setMfaSetupParams,
  validateMfa,
  sendCode,
  setMfaPrepare,
  prepareAddMethod,
  setNewToken,
  generateBackupCodes,
  setMfaIsSetup,
  forcePrepareAddMethod,
  clearTrashToken,
  getMfaSettings,
  removeMethodMfa,
  setMfaEmailSetUpped
} from "../../actions/authActions";

import { setLeftNavigation } from "../../actions/navigationActions";

import FullScreenPageMenu from "../fullScreenPageMenu";
import Footer from "../footer";
import Mfa from "../../mfa";
import {LABEL, MINIMUM_ATTEMPTS, REMAINING_CODE_RESEND_SECONDS} from "../../mfa/constants";

const AT_LEAST_ONE_CONFIGURATION = 1;
const REDIRECT_PATH = PLAN_ROUTE;
const LOGIN_PATH = LOGIN_ROUTE;

const mapStateToProps = (state, ownProps) => {
  return {
    ...ownProps,
    loginInfo: state.auth.loginInfo,
    userName: state.auth.userName,
    mfaSetupParams: state.auth.mfaSetupParams,
    mfaEnabled: state.auth.mfaEnabled,
    mfaType: state.auth.mfaType,
    mfaToken: state.auth?.loginInfo?.mfaToken
      ? state.auth.loginInfo.mfaToken
      : state.auth.mfaToken,
    mfaNotEnabledGracePeriodEnded: state.auth.mfaNotEnabledGracePeriodEnded,
    rememberDeviceDays: state.auth?.loginInfo?.rememberDeviceDays,
    mfaTypesConfigured: state.auth?.mfaTypesConfigured,
    mfaEmailSetUpped: state.auth?.mfaEmailSetUpped,
  };
};

const resolveErrorMessage = (e, defaultError) => {
  if (e.response && e.response.data && e.response.data.errorMessage) {
      return e.response.data.errorMessage;
  }

  return defaultError;
}

const isSameStepConfig = (currentConfig, nextConfig) => (
  currentConfig?.step === nextConfig?.step
  && currentConfig?.disabled === nextConfig?.disabled
  && currentConfig?.backArrow === nextConfig?.backArrow
  && currentConfig?.btnLabel === nextConfig?.btnLabel
);

const hasActiveMfaConfigurationFlow = ({
  mfaSetup,
  changeMethod,
  stepsConfigOverride,
  addAnotherMethod,
  currentStep,
}) => Boolean(
  mfaSetup
  || changeMethod
  || stepsConfigOverride
  || addAnotherMethod
  || currentStep > 0
);

const shouldRedirectCompletedMfaFlow = ({
  authToken,
  mfaEnabled,
  mfaSetup,
  changeMethod,
  stepsConfigOverride,
  addAnotherMethod,
  currentStep,
}) => Boolean(authToken && mfaEnabled && !hasActiveMfaConfigurationFlow({
  mfaSetup,
  changeMethod,
  stepsConfigOverride,
  addAnotherMethod,
  currentStep,
}));

const MfaAuth = (props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const routeState = history?.location?.state || {};
  const initialMfaConfigured = routeState?.mfaConfigured || history?.location?.mfaConfigured || [];
  const [configuredMethods, setConfiguredMethods] = useState(initialMfaConfigured);
  const addAnotherMethod = configuredMethods.length >= AT_LEAST_ONE_CONFIGURATION;
  const mfaRef = useRef(null);
  const [mfaStepConfig, setMfaStepConfig] = useState(mfaRef?.current?.getMfaData() ? mfaRef?.current?.getMfaData() : [{ disabled: false, step: 0, backArrow: undefined }]);
  const [mfaSetup, setMfaSetup] = useState(routeState?.setupMfaFromSettings ?? history?.location?.setupMfaFromSettings);
  const [isMfaProcessing, setIsMfaProcessing] = useState(false);
  const [mfaCodeAreSaved, setMfaCodeAreSaved] = useState(false);
  const [validateMfaCodeError, setValidateMfaCodeError] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);
  const [footerActionDisabled, setFooterActionDisabled] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [changeMethod, setChangeMethod] = useState(routeState?.changeMethod ?? history?.location?.changeMethod);
  const [isMethodChanged, setIsMethodChanged] = useState(false);
  const [stepsConfigOverride, setStepsConfigOverride] = useState(null);
  const [hasReachedMaxCodeRequest, setHasReachedMaxCodeRequest] = useState(false)
  const [remainingCodeResendWindowSeconds, setRemainingCodeResendWindowSeconds] = useState(REMAINING_CODE_RESEND_SECONDS)
  const [remainingLockedOutTimeWindowSeconds, setRemainingLockedOutTimeWindowSeconds] = useState(null);
  const [isCodeEmailVerified, setIsCodeEmailVerified] = useState(false)
  const {
    userName,
    mfaType,
    mfaToken,
    mfaEnabled,
    loginInfo,
    mfaNotEnabledGracePeriodEnded,
    rememberDeviceDays,
    mfaEmailSetUpped
  } = props;

  const handleSetMfaEmailSetUpped = (emailToSendCode) => {
    dispatch(setMfaEmailSetUpped(emailToSendCode));
  }

  const configMFA = () => {
    if(!mfaNotEnabledGracePeriodEnded){
      dispatch(clearTrashToken());
    }
    setMfaSetup(true);
    nextHandler();
  };

  const backHandler = async () => {
    setValidateMfaCodeError(null);

    if (!mfaRef?.current) return;

    if (currentStep === 0) {
      if(routeState?.setupMfaFromSettings ?? history?.location?.setupMfaFromSettings){
        dispatch(setLeftNavigation(true));
        history.push({ pathname: SETTINGS_ROUTE, state:{backToSettings:routeState?.setupMfaFromSettings ?? history?.location?.setupMfaFromSettings} });
      }
      setMfaSetup(false);
    } else if(currentStep === 1) {
      dispatch(clearTrashToken());
      setMfaStepConfig(mfaRef?.current.triggerBackStep());
    } else if (currentStep === 2) {
      if(!mfaEnabled) setMfaStepConfig(mfaRef?.current.triggerBackStep());
    } else {
      setMfaStepConfig(mfaRef?.current.triggerBackStep());
    }
  };

  const handleStepZero = async () => {
    const currentMFAType = mfaRef?.current?.getMfaType() || mfaType
    if (!currentMFAType || !userName) return;
    dispatch(setMfaType(currentMFAType));
    setIsMfaProcessing(true);
    setFooterActionDisabled(true);
    if (!mfaToken && !stepsConfigOverride) {

      try {
        const response = await prepareAddMethod(userName, currentMFAType);
        prepareMethodCallback(response);
      } catch (error) {
        setIsMfaProcessing(false);
      }

    } else if(mfaToken && !stepsConfigOverride) {

      try {
        const response = await forcePrepareAddMethod(userName, currentMFAType, mfaToken, mfaEmailSetUpped);
        prepareMethodCallback(response);
      } catch (error) {
        setIsMfaProcessing(false);
      }

    } else {
      setMfaStepConfig(mfaRef?.current.triggerNextStep());
    }

    setFooterActionDisabled(false);
  }

  const handleStepTwoNotOverrideAndOneMethodChanged = async () => {
    if (addAnotherMethod) {
      // skip backup code, is must be the same as the first method
      setMfaStepConfig(mfaRef?.current.triggerNextStep());
    } else {
      await generateBackupCodeList();
    }
  }

  const handleStepThreeNotOverrideAndTwoMethodChanged = () => {
    if (mfaCodeAreSaved) {
      setMfaStepConfig(mfaRef?.current.triggerNextStep());
      setMfaSetup(false);
    }
  }

  const handleStepSendCode = async () => {
    const currentMFAType = mfaRef?.current?.getMfaType() || mfaType
    if (!currentMFAType || !userName) return;

    setIsMfaProcessing(true);
    setFooterActionDisabled(true);

    try {
      const response = await forcePrepareAddMethod(userName, currentMFAType, mfaToken, mfaEmailSetUpped);
      const codeSent = await sendCodeHandler()
      if (!codeSent) return;
      prepareMethodCallback(response);
    } catch (error) {
      setIsMfaProcessing(false);
    }
  }

  const nextHandler = async () => {
    if (!mfaRef?.current) return;

    if (currentStep === 0) return handleStepZero()

    if (currentStep === 1 && mfaStepConfig?.btnLabel === LABEL.SEND_CODE) return handleStepSendCode()

    if ((currentStep === 2  && !stepsConfigOverride) ||
      (currentStep === 1 && isMethodChanged)) return handleStepTwoNotOverrideAndOneMethodChanged()

    if((currentStep === 3 && !stepsConfigOverride) ||
      (currentStep === 2 && isMethodChanged)) return handleStepThreeNotOverrideAndTwoMethodChanged()

    setMfaStepConfig(mfaRef?.current.triggerNextStep());
  };

  const prepareMethodCallback = (response) => {
    dispatch(setMfaSetupParams(response.data.setup));
    dispatch(setMfaPrepare(response.data));
    setMfaStepConfig(mfaRef?.current.triggerNextStep());
    setIsMfaProcessing(false);
  };

  const setMfaAuthType = (type) => {
    if (props.mfaType !== type) {
      dispatch(setMfaType(type ? type : "TOTP"));
    }
  };

  const sendCodeHandler= async () => {
    try {
      const {data} = await sendCode(userName, mfaType, mfaToken)
      setHasReachedMaxCodeRequest(data.remainingSendCodeAttempts === MINIMUM_ATTEMPTS)
      setRemainingCodeResendWindowSeconds(data.codeResendWindowSeconds)
      setRemainingLockedOutTimeWindowSeconds(data.remainingLockedOutTimeWindowSeconds)
      return true;
    } catch (e) {
      console.log(e)
      setIsMfaProcessing(false);
      setFooterActionDisabled(false);
      setValidateMfaCodeError(resolveErrorMessage(e, 'an unexpected error happened'));
      return false;
    }
  }

  const validateMfaCode = async (authCode) => {
    setIsMfaProcessing(true);
    let code = authCode ? authCode : mfaRef?.current.getMfaCode();
    if (code) {
      try {
        setValidateMfaCodeError(null);
        const response = await validateMfa(userName, code, mfaToken, false, mfaType);
        dispatch(setNewToken(response.data));
        dispatch(setMfaIsSetup(true));
        setIsCodeEmailVerified(true);
        setIsMfaProcessing(false);
        setFooterActionDisabled(false);
      } catch (error) {
        setIsMfaProcessing(false);
        if (error.response) {
          setValidateMfaCodeError(resolveErrorMessage(error, 'Invalid code. Please try again.'));
        } else if (error.request) {
          setValidateMfaCodeError('Unable to check code right now.');
        } else {
          setValidateMfaCodeError('An unexpected error happened');
        }
      }

    }
  };

  const removeMethod = async (password) => {
    try {
      setIsMfaProcessing(true);
      setValidateMfaCodeError(null);

      if(!changeMethod) return;

      const response = await removeMethodMfa(changeMethod, password);
      if (response.status !== 200) return;

      setConfiguredMethods((methods) => methods.filter((method) => method !== changeMethod));
      dispatch(setMfaSetupParams(null));
      dispatch(setMfaPrepare({mfaToken: null}));
      dispatch(clearTrashToken());
      setMfaSetup(true);
      dispatch(setMfaType(null));
      dispatch(setMfaIsSetup(false));
      setChangeMethod(false);
      resetSteps();
      setFooterActionDisabled(false);
      setIsMethodChanged(false)
      setIsMfaProcessing(false);

    } catch (error) {
      setIsMfaProcessing(false);
      setValidateMfaCodeError(
        resolveErrorMessage(error, 'Invalid password. Please try again.')
      );
    }
  }

  const generateBackupCodeList = async () => {
    setIsMfaProcessing(true);
    generateBackupCodes()
      .then((response) => {
        setBackupCodes(response.data.backupCodeList);
        setIsMfaProcessing(false);
        setMfaStepConfig(mfaRef?.current.triggerNextStep());
      })
      .catch((error) => {
        setIsMfaProcessing(false);
      });
  };

  const setMfaSavedCodes = (bool) => {
    setMfaCodeAreSaved(bool);
  };

  const continueToPortal = () => {
    history.push({ pathname: REDIRECT_PATH });
  };

  const resetSteps = () => {
    setCurrentStep(0)
    setMfaStepConfig([{ disabled: false, step: 0, backArrow: undefined }])
  }

  //Init config and data used through MFA flow
  const init = () => {
    if (shouldRedirectCompletedMfaFlow({
      authToken: loginInfo.authToken,
      mfaEnabled,
      mfaSetup,
      changeMethod,
      stepsConfigOverride,
      addAnotherMethod,
      currentStep,
    })) {
      history.push({ pathname: REDIRECT_PATH });
    }
    if (loginInfo?.mfaEnabled) {
      dispatch(setLeftNavigation(false));
    }


    if (mfaRef.current) {
      const nextStepConfig = mfaRef?.current?.getMfaData();
      setIsMfaProcessing(false);
      if (mfaCodeAreSaved) {
        setFooterActionDisabled(false);
      } else {
        setFooterActionDisabled(nextStepConfig?.disabled);
      }

      setMfaStepConfig((prevState) => (
        isSameStepConfig(prevState, nextStepConfig) ? prevState : nextStepConfig
      ));
      setMfaAuthType(mfaRef?.current?.getMfaType());
      setCurrentStep(nextStepConfig?.step);
    }
  };

  useEffect(() => {
    init();
  }, [mfaRef, mfaStepConfig, mfaSetup, mfaCodeAreSaved]);

  const toggleFooterActionDisabled = (disabled) => {
    setFooterActionDisabled(disabled)
  }

  const showFooter = () => {
    const isMFADisabledAndNotStepFour = currentStep !== 4 && mfaSetup
    const isMFASetupRequired = currentStep !== 3 && !changeMethod && mfaSetup && mfaEnabled
    const hideFooter = !isNaN(currentStep) && !(currentStep === 4)
    return isMFADisabledAndNotStepFour || isMFASetupRequired || hideFooter
  }

  return (
    <>
      <div className={'mfaMainContainer'}>
        <FullScreenPageMenu
            showArrow={mfaStepConfig?.backArrow}
            onBack={backHandler}
            center={changeMethod ? '' : (mfaSetup || !loginInfo.mfaEnabled || loginInfo.authToken) ? "Set up two-factor authentication" : "Two-factor authentication"}
            hideSettings={true}
        />
        <Mfa
            {...props}
            ref={mfaRef}
            mfaSetup={mfaSetup}
            configMFA={configMFA}
            setMfaSavedCodes={setMfaSavedCodes}
            mfaCodeAreSaved={mfaCodeAreSaved}
            stepsConfigOverride={stepsConfigOverride}
            validateMfaCode={validateMfaCode}
            validateMfaCodeError={validateMfaCodeError}
            loading={isMfaProcessing}
            mfaIsSetup={loginInfo.mfaEnabled}
            backupCodes={backupCodes}
            continueToPortal={continueToPortal}
            redirectPath={REDIRECT_PATH}
            loginPath={LOGIN_PATH}
            mfaToken={mfaToken}
            setIsMfaProcessing={setIsMfaProcessing}
            getMfaSettings={getMfaSettings}
            setValidateMfaCodeError={setValidateMfaCodeError}
            rememberDeviceDays={rememberDeviceDays}
            withToken={loginInfo.authToken}
            changeMethod={changeMethod}
            removeMethod={removeMethod}
            isMethodChanged={isMethodChanged}
            resetSteps={resetSteps}
            mfaConfigured={configuredMethods}
            addAnotherMethod={addAnotherMethod}
            sendCodeHandler={sendCodeHandler}
            hasReachedMaxCodeRequests={hasReachedMaxCodeRequest}
            isCodeEmailVerified={isCodeEmailVerified}
            remainingCodeResendWindowSeconds={remainingCodeResendWindowSeconds}
            remainingLockedOutTimeWindowSeconds={remainingLockedOutTimeWindowSeconds}
            toggleFooterActionDisabled={toggleFooterActionDisabled}
            setEmailSetUpToMFA={handleSetMfaEmailSetUpped}
        />
        <Footer
            showTopFooter={showFooter()}
            button={
              <Button
                  primary
                  content={mfaStepConfig?.btnLabel || LABEL.NEXT_STEP}
                  disabled={footerActionDisabled}
                  onClick={nextHandler}
                  loading={footerActionDisabled ? !!isMfaProcessing : isMfaProcessing}
              />
            }
        />
      </div>
    </>
  );
};

export default connect(mapStateToProps)(withRouter(MfaAuth));
